import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, ChevronDown, Compass, FileDown, Shield } from 'lucide-react';
import { useAuthStore } from '../store/useStore';

export default function UserMenu() {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const user = useAuthStore((state) => state.user);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  function go(path: string) {
    setIsOpen(false);
    navigate(path);
  }

  function handleLogout() {
    clearAuth();
    navigate('/login');
  }

  const item = 'w-full flex items-center gap-3 px-4 py-2 text-sm text-ink-secondary hover:bg-surface-sunken border-b border-edge-subtle last:border-b-0';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1.5 hover:bg-surface-sunken"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {user?.avatar ? (
          <img
            src={user.avatar}
            alt={user.username}
            className="w-7 h-7 rounded-full object-cover avatar-duotone"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-ink text-canvas flex items-center justify-center font-mono text-xs">
            {user?.username?.[0]?.toUpperCase() || 'U'}
          </div>
        )}
        <span className="kicker text-ink hidden sm:inline">
          {user?.displayName || user?.username}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-ink-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-52 bg-canvas border border-edge z-50 animate-fade-in" role="menu">
          <button onClick={() => go('/discover')} className={item}>
            <Compass className="w-4 h-4" />
            Discover feeds
          </button>
          <button onClick={() => go('/settings')} className={item}>
            <Settings className="w-4 h-4" />
            Settings
          </button>
          <button onClick={() => go('/export')} className={item}>
            <FileDown className="w-4 h-4" />
            Export
          </button>
          {user?.isAdmin && (
            <button onClick={() => go('/admin')} className={item}>
              <Shield className="w-4 h-4" />
              Admin
            </button>
          )}
          <button onClick={handleLogout} className={`${item} text-signal`}>
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
